import React, { FC, ReactNode, useCallback } from 'react';
import { useTranslation } from 'services/localization';
import { stringifyArray } from 'services/stringify';
import { selectActionResult } from '../../preferencesSelectors';
import { closeNotification } from '../../preferencesSlice';
import { ActionType, ResultStatus } from '../../types';
import { ErrorAcceptGmoi } from './components/ErrorAcceptGmoi';
import { ErrorRequest } from './components/ErrorRequest';
import { SuccessGlobalUnsubscribe } from './components/SuccessGlobalUnsubscribe';
import { SuccessSubscribeKeys } from './components/SuccessSubscribeKeys';
import { SuccessUnsubscribeKeys } from './components/SuccessUnsubscribeKeys';
import { SuccessUpdatePreferences } from './components/SuccessUpdatePreferences';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { Box, xcss } from '@atlaskit/primitives';
import { SuccessWithWarning } from './components/SuccessWithWarning';
import { ProductInvitationsWarning } from './components/ProductInvitationsWarning';

const actionNotificationStyles = xcss({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 'space.100',
  marginBottom: 'space.200',
});

const resolveSuccessActionResult = (key: ActionType, onClose: () => void, data?: string | undefined): ReactNode => {
  switch (key) {
    case 'unsubscribeAll':
      return (
        <SuccessWithWarning success={SuccessGlobalUnsubscribe} warning={ProductInvitationsWarning} onClose={onClose} />
      );
    case 'updatePreferences':
      return <SuccessUpdatePreferences onClose={onClose} />;
    case 'unsubscribeKeys':
      return <SuccessUnsubscribeKeys onClose={onClose} data={data} />;
    case 'subscribeKeys':
      return <SuccessSubscribeKeys onClose={onClose} data={data} />;
    default:
      return null;
  }
};

const resolveErrorActionResult = (key: ActionType, onClose: () => void): ReactNode => {
  switch (key) {
    case 'acceptGmoi':
      return <ErrorAcceptGmoi onClose={onClose} />;
    case 'request':
      return <ErrorRequest onClose={onClose} />;
    default:
      return null;
  }
};

const resolveActionResultStatus = (
  status: ResultStatus,
): ((key: ActionType, onClose: () => void, data?: string | undefined) => ReactNode) => {
  switch (status) {
    case 'success':
      return resolveSuccessActionResult;
    case 'error':
      return resolveErrorActionResult;
  }
};

const ActionNotification: FC = () => {
  const dispatch = useAppDispatch();
  const messageCloseHandler = useCallback(() => dispatch(closeNotification()), [dispatch]);

  const actionResult = useAppSelector(selectActionResult);
  const { translate, keyWithTitle } = useTranslation();

  if (actionResult == null) return null;

  const subscriptionsWithGroup = [];
  const actionResultData = actionResult.data;
  if (actionResultData !== null && actionResultData !== undefined) {
    for (const subscriptions of actionResultData) {
      // Subscriptions will be in the format ['groupKey1:subscriptionKey1', 'groupKey2:subscriptionKey2', ...]
      // groupKey will be empty for the hidden subscriptions
      for (const subscription of subscriptions.split(',')) {
        const groupAndSubscriptionKey = subscription.split(':');
        const groupKey = groupAndSubscriptionKey[0];
        const subscriptionKey = groupAndSubscriptionKey[1];

        // For hidden subscriptions only use the subscriptionKey for the message
        if (groupKey) {
          subscriptionsWithGroup.push(
            translate(keyWithTitle(groupKey)) + ': ' + translate(keyWithTitle(subscriptionKey)),
          );
        } else {
          subscriptionsWithGroup.push(translate(keyWithTitle(subscriptionKey)));
        }
      }
    }
  }

  const notification = resolveActionResultStatus(actionResult.status)(
    actionResult.key,
    messageCloseHandler,
    stringifyArray(subscriptionsWithGroup),
  );

  return (
    <Box xcss={actionNotificationStyles} testId='preferences-center-action-notification'>
      {notification}
    </Box>
  );
};

export default ActionNotification;
